/**
 * WebhookFailureDetailExtra — the detail-exclusive body for a single webhook
 * failure, rendered as the resource's `diagnosis` tab on the framework detail
 * panel's form rail (#1417, moved onto the rail by #1804). The provider / topic / error-kind / received-at fields are rendered
 * by the framework's generic detail grid (from the resource `fields`); this
 * component owns only what the grid does NOT show: the FULL error-message head,
 * the resolved status, the trace id, the related-event link, and the
 * raw-payload panel.
 *
 * It is PURELY presentational — it reads everything (including `raw_payload`)
 * from the `failure` record the panel already fetched via the resource's
 * `api.detail`, so the two surfaces (drawer + standalone page) share ONE detail
 * fetch and there is no second GET of the same record (#1417 P3). The drawer
 * passes the clicked LIST row first (instant paint, no `raw_payload` yet) and
 * re-renders with the full detail once `api.detail` resolves; the standalone
 * page renders straight from the detail response.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge } from '@resource-kit/ui/react';
import { JsonPanel } from '@components/ui/JsonPanel';
import { webhookEventPath } from '@lib/routes';
import {
  selfHealBadgeVariantFor,
  selfHealCriterionKeyFor,
  selfHealExplanationKeyFor,
  selfHealLabelKeyFor,
} from './webhookFailureBadges';
import type { WebhookFailureDetail, WebhookFailureListItem } from '@/services/webhook-failures';

export interface WebhookFailureDetailExtraProps {
  /**
   * The failure record from the panel's `api.detail` fetch. Typed as the list
   * item (the resource row type); the detail response is a superset that also
   * carries `raw_payload`, read defensively below.
   */
  failure: WebhookFailureListItem;
}

export function WebhookFailureDetailExtra({ failure }: WebhookFailureDetailExtraProps) {
  const { t } = useTranslation();

  const resolved = failure.resolved_at != null;
  // The full detail record carries `raw_payload`; the pre-fetch list row does
  // not have the key at all. Its presence is the "detail loaded" signal — until
  // then the panel is still resolving `api.detail` and we show a placeholder.
  const hasDetail = 'raw_payload' in failure;
  const rawPayload = (failure as WebhookFailureDetail).raw_payload;

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <DetailRow label={t('webhookFailures.detail.status')}>
          {resolved ? t('webhookFailures.detail.resolved') : t('webhookFailures.detail.unresolved')}
        </DetailRow>
        <DetailRow label={t('webhookFailures.detail.traceId')}>
          <span className="break-all font-mono text-xs">{failure.trace_id}</span>
        </DetailRow>
      </dl>

      <SelfHealSection failure={failure} />

      <div className="space-y-1">
        <div className="text-[11px] font-medium text-muted-foreground">
          {t('webhookFailures.col.errorMessage')}
        </div>
        {/*
          No height cap and no scrollbar: `error_message_head` is capped at 200
          characters by the backend (ADR-0004 — the raw provider error never
          lands in the row), so it cannot run away, and a scroll container
          around four lines of text inside a panel with room to spare only
          hides the thing the operator opened this tab to read.
        */}
        <pre className="whitespace-pre-wrap break-words rounded-md border bg-surface-1 p-2 font-mono text-xs">
          {failure.error_message_head}
        </pre>
      </div>

      <div className="space-y-1">
        <div className="text-[11px] font-medium text-muted-foreground">
          {t('webhookFailures.detail.relatedEvent')}
        </div>
        {failure.source_webhook_event_id ? (
          <Link
            to={webhookEventPath(failure.source_webhook_event_id)}
            className="text-sm text-primary underline-offset-2 hover:underline"
          >
            {t('webhookFailures.detail.viewEvent')}
          </Link>
        ) : (
          <span className="text-sm text-muted-foreground">
            {t('webhookFailures.detail.noRelatedEvent')}
          </span>
        )}
      </div>

      <div className="min-h-0 flex-1">
        {hasDetail ? (
          <JsonPanel
            value={rawPayload ?? ''}
            ariaLabel={t('webhookFailures.detail.rawPayload')}
            label={t('webhookFailures.detail.rawPayload')}
          />
        ) : (
          <p className="text-sm text-muted-foreground">{t('webhookFailures.detail.loading')}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Whether a later delivery already did what this failed write could not, and
 * what proved it (#2017).
 *
 * It sits above the error message on purpose: an operator opening a
 * `d1_write_failed` row wants "can I just close this?" answered before they
 * start reading a stack trace. The link is the CHECK — the verdict is the
 * backend's inference from stored evidence, and the matched event is the
 * evidence, one click away.
 *
 * `self_heal` rides the LIST row too, so this paints on the drawer's instant
 * first pass; it is deliberately not gated on the `raw_payload` detail fetch
 * the way the payload panel below is.
 *
 * `null` renders NOTHING — the backend only judges open `d1_write_failed` rows,
 * and a "not assessed" line on every other row would be noise that reads like a
 * verdict.
 */
function SelfHealSection({ failure }: { failure: WebhookFailureListItem }) {
  const { t } = useTranslation();
  const selfHeal = failure.self_heal;
  const verdictKey = selfHeal ? selfHealLabelKeyFor(selfHeal.verdict) : undefined;
  if (!selfHeal || !verdictKey) return null;

  // The criterion is the specific reason; the verdict's own line is the
  // fallback for the case that HAS no criterion (`needs_review`).
  const criterionKey = selfHeal.criterion ? selfHealCriterionKeyFor(selfHeal.criterion) : undefined;
  const explanationKey = criterionKey ?? selfHealExplanationKeyFor(selfHeal.verdict);

  return (
    <div className="space-y-1">
      <div className="text-[11px] font-medium text-muted-foreground">
        {t('webhookFailures.detail.selfHeal')}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant={selfHealBadgeVariantFor(selfHeal.verdict)}
          className="text-[10px] uppercase"
        >
          {t(verdictKey)}
        </Badge>
        {explanationKey ? (
          <span className="text-sm text-muted-foreground">{t(explanationKey)}</span>
        ) : null}
      </div>
      {selfHeal.matched_event_id ? (
        <Link
          to={webhookEventPath(selfHeal.matched_event_id)}
          className="inline-block text-sm text-primary underline-offset-2 hover:underline"
        >
          {t('webhookFailures.detail.viewMatchedEvent')}
        </Link>
      ) : null}
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-foreground">{children}</dd>
    </>
  );
}
